const CustomCartConfetti = (() => {
  const COLOURS = ['#6cb5a4', '#1f5f4f', '#f2c14e', '#e8798b', '#7f9cf5', '#f0f4f3'];
  const PARTICLE_COUNT = 90;
  const GRAVITY = 0.16;
  const DRAG = 0.995;
  const LIFETIME = 2600;

  const running = new WeakMap();

  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const randomBetween = (min, max) => min + Math.random() * (max - min);

  const createParticles = (width) => Array.from({ length: PARTICLE_COUNT }, () => ({
    x: randomBetween(0, width),
    y: randomBetween(-140, -10),
    width: randomBetween(5, 11),
    height: randomBetween(7, 14),
    velocityX: randomBetween(-0.9, 0.9),
    velocityY: randomBetween(1.4, 3.6),
    rotation: randomBetween(0, Math.PI * 2),
    spin: randomBetween(-0.16, 0.16),
    colour: COLOURS[Math.floor(Math.random() * COLOURS.length)],
  }));

  const sizeCanvas = (canvas) => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    return { ratio, width, height };
  };

  const canvasBurst = (canvas) => {
    if (!canvas || prefersReducedMotion()) return;

    const previous = running.get(canvas);
    if (previous) window.cancelAnimationFrame(previous);

    const context = canvas.getContext('2d');
    if (!context) return;

    const { ratio, width, height } = sizeCanvas(canvas);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const particles = createParticles(width);
    const startedAt = performance.now();
    canvas.classList.add('is-active');

    const frame = (now) => {
      const elapsed = now - startedAt;
      const fade = Math.max(0, 1 - Math.max(0, elapsed - LIFETIME * 0.6) / (LIFETIME * 0.4));

      context.clearRect(0, 0, width, height);
      context.globalAlpha = fade;

      for (const particle of particles) {
        particle.velocityY += GRAVITY;
        particle.velocityX *= DRAG;
        particle.x += particle.velocityX;
        particle.y += particle.velocityY;
        particle.rotation += particle.spin;

        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(particle.rotation);
        context.fillStyle = particle.colour;
        context.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
        context.restore();
      }

      if (elapsed < LIFETIME) {
        running.set(canvas, window.requestAnimationFrame(frame));
        return;
      }

      context.clearRect(0, 0, width, height);
      canvas.classList.remove('is-active');
      running.delete(canvas);
    };

    running.set(canvas, window.requestAnimationFrame(frame));
  };

  const SHOW_FOR = 4600;

  let loading = null;        // the one load attempt, in flight or settled
  let animationData = null;
  let instance = null;
  let hiding = null;

  const loadPlayer = (src) => new Promise((resolve, reject) => {
    // Already on the page, from an earlier call or anything else that vendored it.
    if (window.lottie) {
      resolve(window.lottie);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => (
      window.lottie ? resolve(window.lottie) : reject(new Error('lottie did not register'))
    ));
    script.addEventListener('error', () => reject(new Error(`could not load ${src}`)));
    document.head.append(script);
  });

  const load = (surface) => {
    if (loading) return loading;

    const player = surface?.dataset.player;
    const animation = surface?.dataset.animation;
    if (!player || !animation) return Promise.resolve(false);

    loading = Promise.all([
      loadPlayer(player),
      fetch(animation).then((response) => {
        if (!response.ok) throw new Error(`${animation} responded ${response.status}`);
        return response.json();
      }),
    ])
      .then(([, data]) => {
        animationData = data;
        return true;
      })
      .catch((error) => {
        console.warn('[custom-cart] confetti is using the canvas fallback:', error.message);
        return false;
      });

    return loading;
  };

  const teardown = (surface) => {
    surface.classList.remove('is-active');
    instance?.destroy();
    instance = null;
  };

  const lottieBurst = (surface) => {
    if (!window.lottie || !animationData || !surface) return false;

    window.clearTimeout(hiding);
    instance?.destroy();
    surface.classList.add('is-active');

    instance = window.lottie.loadAnimation({
      container: surface,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
        progressiveLoad: true,
      },
    });

    hiding = window.setTimeout(() => teardown(surface), SHOW_FOR);
    return true;
  };

  const surfaces = (root) => ({
    lottie: root?.querySelector('[data-cc-confetti-surface]') ?? null,
    canvas: root?.querySelector('[data-cc-confetti]') ?? null,
  });

  const preload = (root) => {
    if (prefersReducedMotion()) return;
    const { lottie } = surfaces(root);
    if (!lottie) return;

    const start = () => load(lottie);
    if (window.requestIdleCallback) window.requestIdleCallback(start, { timeout: 2000 });
    else window.setTimeout(start, 300);
  };

  const burst = (root) => {
    if (prefersReducedMotion()) return;
    const { lottie, canvas } = surfaces(root);

    if (lottieBurst(lottie)) return;

    canvasBurst(canvas);
    load(lottie);   // ready for next time, if it simply had not arrived yet
  };

  return { burst, preload };
})();

window.CustomCartConfetti = CustomCartConfetti;
